import React, { useState, useCallback, useMemo, useRef } from 'react'
import { Grid } from '@material-ui/core'
import PropTypes from 'prop-types'
import OpalCard from '../../../../../opal/OpalCard'
import InlineCardHeader from '../../../../../molecules/Card/InlineCardHeader'
import SydButton from '../../../../../commonDesign/Button'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import AutoTable from '../../QueryTool/AutoTable'
import { useExecuteQueryBlueprint } from '../../../../../../api/denali'
import { decipherError } from '../../../../../../utils/decipherError'
import ParameterInputForm from './ParameterInputForm'

function TestExecutionTab ({
  blueprint,
  selectedVersion,
  onSelectVersion,
  versionData,
  allVersions
}) {
  const paramFormRef = useRef(null)

  // Use versionData (selected version) instead of blueprint for execution
  const displayData = versionData || blueprint
  const currentDefinition = displayData.definition

  const purposes = useMemo(() => currentDefinition?.data?.purposes || {}, [currentDefinition])
  const purposeCodes = Object.keys(purposes)

  const [selectedPurpose, setSelectedPurpose] = useState(purposeCodes[0] || '')
  const [pageSize, setPageSize] = useState(100)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const { mutateAsync: executeBlueprint, isLoading: executing } = useExecuteQueryBlueprint()

  const purposeData = purposes[selectedPurpose]
  const requiredParams = useMemo(() => {
    if (!purposeData?.masterPredicate) return []
    return purposeData.masterPredicate.requiredParams || []
  }, [purposeData])

  // Version selector options with useMemo
  const testVersionOptions = useMemo(() => {
    return (allVersions || []).map(v => {
      const isActiveVer = v.definitionVersionId === blueprint.definitionVersionId
      const dateStr = v.versionDate ? new Date(v.versionDate).toLocaleDateString() : ''
      const descStr = v.description ? ` - ${v.description}` : ''
      const activeLabel = isActiveVer ? ' (active)' : ''

      return {
        label: `${v.definitionVersionId}${activeLabel}${descStr}${dateStr ? ` • ${dateStr}` : ''}`,
        value: v.definitionVersionId
      }
    })
  }, [allVersions, blueprint.definitionVersionId])

  // Current selected value (version ID)
  const testSelectedValue = selectedVersion?.definitionVersionId || blueprint.definitionVersionId

  const handleExecute = useCallback(async () => {
    // Get parameters from form imperatively
    const parameters = paramFormRef.current ? paramFormRef.current.getValues() : {}

    try {
      setError(null)
      const executionResult = await executeBlueprint({
        blueprintId: blueprint.blueprintId,
        definitionVersionId: selectedVersion?.definitionVersionId,
        purposeCode: selectedPurpose,
        parameters,
        page: 0,
        pageSize,
        debug: true // Debug mode bypasses enabled check for testing
      })
      setResult(executionResult)
    } catch (err) {
      setError(decipherError(err))
      setResult(null)
    }
  }, [blueprint.blueprintId, selectedVersion, selectedPurpose, pageSize, executeBlueprint])

  if (!currentDefinition) {
    return (
      <OpalCard size='sm'>
        <InlineCardHeader>Test Blueprint Execution</InlineCardHeader>
        <div style={{ padding: '16px' }}>
          <p>This blueprint has no active definition. Add a definition to test execution.</p>
        </div>
      </OpalCard>
    )
  }

  if (purposeCodes.length === 0) {
    return (
      <OpalCard size='sm'>
        <InlineCardHeader>Test Blueprint Execution</InlineCardHeader>
        <div style={{ padding: '16px' }}>
          <p>This definition has no purposes configured. Add purposes by parsing DQL queries.</p>
        </div>
      </OpalCard>
    )
  }

  return (
    <OpalCard size='sm'>
      <InlineCardHeader>Test Blueprint Execution</InlineCardHeader>

      <div style={{ padding: '16px' }}>
        <Grid container spacing={2}>
          {/* Version Selector */}
          <Grid item xs={12} md={6}>
            <SydLabel label='Test Version'>
              <SydSelect
                options={testVersionOptions}
                value={testSelectedValue}
                onChange={e => {
                  const versionId = e.target.value
                  if (versionId === blueprint.definitionVersionId) {
                    onSelectVersion(null)
                  } else {
                    const version = (allVersions || []).find(v => v.definitionVersionId === versionId)
                    onSelectVersion(version)
                  }
                }}
              />
            </SydLabel>
          </Grid>

          {/* Purpose Selector - always show when purposes exist */}
          <Grid item xs={12} md={6}>
            <SydLabel required label='Purpose'>
              <SydSelect
                options={purposeCodes.map(code => ({
                  label: `${purposes[code].displayName} (${code})`,
                  value: code
                }))}
                value={selectedPurpose}
                onChange={e => setSelectedPurpose(e.target.value)}
              />
            </SydLabel>
          </Grid>

          {/* Parameters Section */}
          <Grid item xs={12}>
            <ParameterInputForm
              ref={paramFormRef}
              requiredParams={requiredParams}
            />
          </Grid>

          {/* Execution Options */}
          <Grid item xs={12} md={6}>
            <SydLabel label='Page Size' helperText='Number of rows to return'>
              <SydInput
                type='number'
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value) || 100)}
                min={1}
                max={1000}
              />
            </SydLabel>
          </Grid>

          {/* Execute Button */}
          <Grid item xs={12}>
            <SydButton
              onClick={handleExecute}
              processing={executing}
              variant='primary'
              icon='play_arrow'
            >
              Execute Blueprint
            </SydButton>
          </Grid>

          {/* Error Display */}
          {error && (
            <Grid item xs={12}>
              <div style={{
                padding: '12px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                color: '#c33'
              }}
              >
                <strong>Error:</strong> {error}
              </div>
            </Grid>
          )}

          {/* Results Section */}
          {result && result.data && (
            <Grid item xs={12}>
              <h4>Results ({result.data.length} rows returned)</h4>
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              >
                <AutoTable data={result.data} />
              </div>
            </Grid>
          )}
        </Grid>
      </div>
    </OpalCard>
  )
}

TestExecutionTab.propTypes = {
  blueprint: PropTypes.object.isRequired,
  selectedVersion: PropTypes.object,
  onSelectVersion: PropTypes.func.isRequired,
  versionData: PropTypes.object,
  allVersions: PropTypes.array
}

export default TestExecutionTab
